import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Truck, ArrowRight } from "lucide-react";
import { getCheckoutStatus } from "../lib/api";
import { useCart } from "../context/CartContext";

const MAX_ATTEMPTS = 10;
const POLL_MS = 2000;

export const PaymentStatusPage = () => {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const orderId = sp.get("order_id");
  const method = sp.get("method");

  const [state, setState] = useState({ status: "polling", data: null, message: null });
  const { clear } = useCart();
  const clearedRef = useRef(false);

  useEffect(() => {
    if (method === "cod") {
      if (!clearedRef.current) {
        clear();
        clearedRef.current = true;
      }
      setState({ status: "cod_confirmed", data: { order_id: orderId } });
      return;
    }

    if (!sessionId) {
      setState({ status: "error", message: "Λείπει το session id." });
      return;
    }

    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      try {
        const data = await getCheckoutStatus(sessionId);
        if (cancelled) return;

        if (data.payment_status === "paid") {
          if (!clearedRef.current) {
            clear();
            clearedRef.current = true;
          }
          setState({ status: "paid", data });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", data });
          return;
        }
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setState({ status: "timeout", data });
          return;
        }
        setTimeout(poll, POLL_MS);
      } catch (e) {
        if (!cancelled) setState({ status: "error", message: "Αποτυχία ελέγχου κατάστασης πληρωμής." });
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, method, orderId]);

  const { status, data, message } = state;

  return (
    <main className="min-h-[70vh] flex items-center justify-center px-6 py-20" data-testid="payment-status-page">
      <div className="max-w-xl w-full bg-[#050505] border border-white/5 rounded-3xl p-8 md:p-12 text-center">
        {status === "polling" && (
          <div className="flex flex-col items-center gap-5">
            <Loader2 size={48} className="text-[#B026FF] animate-spin" />
            <div className="font-heading font-bold text-2xl">Επαλήθευση πληρωμής...</div>
            <p className="text-zinc-400 text-sm">Παρακαλώ περιμένετε. Μην κλείσετε αυτή τη σελίδα.</p>
          </div>
        )}

        {status === "paid" && (
          <div className="flex flex-col items-center gap-5" data-testid="payment-success">
            <CheckCircle2 size={56} className="text-[#00FFFF] neon-glow-cyan" />
            <div className="font-heading font-bold text-3xl">Η πληρωμή ολοκληρώθηκε!</div>
            <p className="text-zinc-400">
              Ευχαριστούμε για την παραγγελία σας στο GKLed. Θα λάβετε email επιβεβαίωσης σύντομα.
            </p>
            {data?.order_id && (
              <div className="text-xs text-zinc-500">
                Κωδικός παραγγελίας: <span className="text-white">{data.order_id}</span>
              </div>
            )}
            <Link
              to="/artworks"
              className="mt-4 inline-flex items-center gap-2 bg-[#B026FF] text-black px-6 py-3 rounded-lg font-bold"
              data-testid="back-to-shop"
            >
              Συνέχεια αγορών <ArrowRight size={16} />
            </Link>
          </div>
        )}

        {status === "cod_confirmed" && (
          <div className="flex flex-col items-center gap-5" data-testid="payment-cod-success">
            <Truck size={56} className="text-[#B026FF] neon-glow-pink" />
            <div className="font-heading font-bold text-3xl">Παραγγελία καταχωρήθηκε!</div>
            <p className="text-zinc-400">
              Θα επικοινωνήσουμε μαζί σας για επιβεβαίωση. Θα πληρώσετε κατά την παράδοση.
            </p>
            {orderId && (
              <div className="text-xs text-zinc-500">
                Κωδικός παραγγελίας: <span className="text-white">{orderId}</span>
              </div>
            )}
            <Link
              to="/artworks"
              className="mt-4 inline-flex items-center gap-2 bg-[#B026FF] text-black px-6 py-3 rounded-lg font-bold"
            >
              Συνέχεια αγορών <ArrowRight size={16} />
            </Link>
          </div>
        )}

        {(status === "expired" || status === "timeout" || status === "error") && (
          <div className="flex flex-col items-center gap-5" data-testid="payment-failed">
            <XCircle size={56} className="text-red-500" />
            <div className="font-heading font-bold text-3xl">
              {status === "expired" ? "Η συνεδρία έληξε" : "Κάτι πήγε στραβά"}
            </div>
            <p className="text-zinc-400">
              {message || "Η πληρωμή δεν ολοκληρώθηκε. Μπορείτε να δοκιμάσετε ξανά."}
            </p>
            <Link
              to="/checkout"
              className="mt-4 inline-flex items-center gap-2 border border-white/20 text-white px-6 py-3 rounded-lg font-medium hover:border-[#B026FF]"
            >
              Δοκιμή ξανά
            </Link>
          </div>
        )}
      </div>
    </main>
  );
};

export default PaymentStatusPage;
